import {
    keyBy as _keyBy,
    sortBy as _sortBy,
    uniqBy as _uniqBy,
    get as _get,
    isEqual as _isEqual,
    groupBy as _groupBy,
} from '@lodash';

import i18n from '@app/localization/i18n';
import { getLottoFlagCode } from '@src/data/lottoFlagsMapping';
import { INode } from '@app/utils/sidebar';
import { IState } from '@app/store';
import slugify from '@core/utils/url/slugify';
import { Dispatch } from 'vuex';
import { IFilterState, ILottery } from './types';
import { pushToBreadcrumbIds } from '@src/app/utils/sidebar';
import { SET_SELECTED_TREE_ITEMS } from './mutationTypes';
import LottoEvent from '@core/models/lotto/LottoEvent';
import StoreUtil from '@store/StoreUtil';

import {
    ALL_LOTTERIES_INDEX,
    ALL_LOTTERIES,
    NO_FILTER
} from './common';

// flag set to undefined will be shown as matching icon in sidebar
// if flag is defined than it should be set on value that can be found among flags
export const ALL_LOTTERIES_NODE = {
    id: ALL_LOTTERIES_INDEX,
    name: ALL_LOTTERIES,
    flag: undefined,
    children: [],
    eventCount: 0,
    order: ALL_LOTTERIES_INDEX,
};

function createLotteriesFromEvents(events: LottoEvent[]): ILottery[] {
    return events.map((event: LottoEvent) => ({
        name: event.fullName,
        id: event.lottoId,
    }));
}

export const mutations = {
    // see SET_SELECTED_FEATURED_ITEM in featuredFilter
    [SET_SELECTED_TREE_ITEMS](state: IFilterState, selectedTreeItems: INode[]) {
        state.selectedFeaturedItem = null;
        state.selectedTreeItems = selectedTreeItems;
    },
};

export const getters = {
    /**
     * This getter returns tree of lotteries.
    */
    tree(state: IFilterState, getters: any): INode[] {
        // if getLottoFlagCode don't find correct flag, new flag will be returned
        const createGameNode = (data: any) => ({
            id: data.id,
            name: data.name,
            children: {},
            eventCount: 0,
            order: data.id,
            flag: getLottoFlagCode(data.id)
        }) as INode;

        const createCountryNode = (countryName: string, id: number) => ({
            id,
            flag: countryName,
            name: i18n.t(countryName.toLowerCase()),
            children: _keyBy(lotteryNodes[countryName], 'name'),
            eventCount: _get(lotteryNodes[countryName], 'length', 0),
            order: id,
        }) as any; // TODO update interface. Is not INode

        const addOrUpdateNode = (tree: any, node: INode) => {
            const current = tree.find((item: any) => item.name === node.name);
            if (!current) {
                tree.push(node);
                return;
            }
            current.children = { ...current.children, ...node.children };
        };
        // we group lotteryNodes by flag because it is only way to know which lottery
        // belongs to which country :(
        const lotteries = Object.values(getters.lotteries)
            .map(createGameNode);
        const lotteryNodes = _groupBy(lotteries, 'flag');

        let tree = Object.keys(lotteryNodes).map(createCountryNode);

        // adding elements that may not be in sidebar because of time filter change
        const [countryNode, lotteryNode] = getters.selectedTreeItems;
        if (! countryNode && !lotteryNode) {
            return [ALL_LOTTERIES_NODE, ..._sortBy(tree, 'name')];
        }

        // if persisted node is all-lotteries then ignore it
        if (countryNode.name !== ALL_LOTTERIES) {
            // remove other lotteries of persisted node
            countryNode.children = lotteryNode ? { [lotteryNode.name]: lotteryNode } : {};
            addOrUpdateNode(tree, countryNode);
        }

        tree = _sortBy(tree, 'name').map((node, index) => {
            node.id = index;
            node.order = index;
            return node;
        });
        return [ALL_LOTTERIES_NODE, ...tree];
    },
    /**
     * This getter returns ids of lotteries that are visible in the grid. Which lotteries(and events) are visible
     * in the grid is controlled by sidebar filter.
     *
     * Getter is needed because the events are shown in the event grid if their lotto id is included
     * in returned array;
     *
     * Example:
     * if 'canada' is active, will return ids of every canada lottery [canada_lotto_id, ontario_id, quebec_id, ...]
     * if 'canada' and 'canada atlantic bucko' is selected, getter will return [id_of_canada, id_of_bucko]
     *
     * @see tree getter
     * @see breadcrumbIds getter and spot the difference in examples
     */
    visibleLotteryIds(state: IFilterState, getters: any, rootState: any, rootGetters: any): number[] {
        const { gameSlug, countrySlug } = rootGetters['navigation/route'].params;
        if (!countrySlug && !getters.selectedTreeItemIds) {
            return [];
        }
        if (gameSlug) {
            return getters.lotteries
                .filter((game: ILottery) => slugify(game.name) === gameSlug)
                .map((game: ILottery) => game.id);
        }
        if (countrySlug) {
            return getters._countryToIdsMap[countrySlug];
        }
        return getters.selectedTreeItemIds || [];
    },

    /**
     * This getter returns ids of lotteries that are active in the tree.
     * Example:
     * if 'canada' and 'canada atlantic bucko' is selected, getter will return [id_of_canada, id_of_bucko]
     * if 'canada' is active, will return [id_of_canada]
     *
     * @see tree getter
     * @see visibleLotteryIds getter and spot the difference in examples
     */
    breadcrumbIds(state: IFilterState, getters: any, rootState: IState, rootGetters: any) {
        const {
            countrySlug,
            gameSlug,
        } = rootGetters['navigation/route'].params;
        const tree = getters.tree;

        if (getters.selectedFeaturedItem) {
            return [];
        }

        const breadCrumbIds: number[] = [];
        const country = pushToBreadcrumbIds(tree, countrySlug, breadCrumbIds);
        if (!country || country.id === ALL_LOTTERIES_INDEX) {
            return [ALL_LOTTERIES_INDEX];
        }

        pushToBreadcrumbIds(Object.values(country.children), gameSlug, breadCrumbIds);
        return breadCrumbIds;
    },

    /**
     * This getter returns all available lotteries.
     *
     * For example:
     * : {name: "Italia Keno 10e Loto - 20/90", id: 83}
     * : {name: "Win For Life - 10/20", id: 9}
     */
    lotteries: (state: IFilterState, getters: any): ILottery[] =>
        _uniqBy(createLotteriesFromEvents(getters._lottoEventsFilteredByDate), 'id'),

    selectedTreeItems: StoreUtil.createSimpleGetter('selectedTreeItems'),

    selectedTreeItemIds(state: IFilterState, getters: any): number[] {
        const [countryNode, lotteryNode]: [
            INode,
            INode,
        ] = getters.selectedTreeItems;
        if (lotteryNode) {
            return [lotteryNode.id];
        }
        if (countryNode) {
            return Object.values(countryNode.children).map((child) => child.id);
        }
        return [];
    },

    /**
     * This getter returns true if any lottery game is selected and lottery filter is active. Lottery is selected
     * if its slug can be found in url.
     */
    isAnyLotterySelected(state: IFilterState, getters: any, rootState: IState, rootGetters: any): boolean {
        // TODO update with popular lotteries
        return !_isEqual(getters.breadcrumbIds, [ALL_LOTTERIES_INDEX]) ;
    },

    /**
     * Returns a predicate used to filter lotto events by lotteries.
     * The predicate depends on the currently selected lotteries.
     */
    lotteryFilter(state: IFilterState, getters: any) {
        // if there is no lottery preference, we do not filter available events
        if (!getters.isAnyLotterySelected) {
            return NO_FILTER;
        }

        return (event: LottoEvent) => getters.visibleLotteryIds.includes(event.lottoId);
    },

    _countryToIdsMap: (state: IFilterState, getters: any): Record<string, number[]> => {
        const idsByCountry: Record<string, number[]> = {};
        // can't use keyBy because I'm remapping keys and values
        getters.tree.forEach((node: INode) => {
            idsByCountry[slugify(node.name)] = Object.values(node.children).map((child) => child.id);
        });
        return idsByCountry;
    }
};

export const actions =  ({
    lotteryFilterRoute
}: {
    lotteryFilterRoute: string,
}) => ({
    setSelectedLotteries({ dispatch, rootGetters }: { dispatch: Dispatch, rootGetters: any }, selectedItems: any[]) {
        const [country, game] = selectedItems;

        const params = {} as any;

        if (country) {
            params.countrySlug = slugify(country.name);
        }

        if (game) {
            params.gameSlug = slugify(game.name);
        }
        const { timeFilter, date } = rootGetters['navigation/route'].params;
        if (timeFilter) {
            params.timeFilter = timeFilter;
        } else if (date) {
            params.date = date;
        }

        dispatch('setSelectedTreeItems', selectedItems);
        /*
            Replace is used because the user doesn't expect to navigate through the
            sport menu with the browser back/forward arrows
        */
        dispatch('navigation/push', { params, name: lotteryFilterRoute }, { root: true });
    },

    setSelectedTreeItems: StoreUtil.createSimpleMutatorAction(SET_SELECTED_TREE_ITEMS),
});



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--12!./src/app/store/modules/ui/shared/lotto/filter/lotteryFilter.ts